import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Grid } from '../components/ui/Grid';
import { Swiper } from '../components/ui/Swiper';
import { LoadMore } from '../components/ui/LoadMore';
import { Headline } from '../components/ui/Headline';
import { MusicianPanel } from '../components/MusicianPanel';
import { Filters } from '../components/Filters';
import { SEOHead } from '../components/SEOHead';
import { useLocation } from '../hooks/useLocation';
import { fetchMusicians, fetchFeaturedMusicians, type MusicianWithDistance } from '../services/api/musicians';
import { GENRES, SKILLS, MUSICIAN_STATUSES } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';

const ITEMS_PER_PAGE = 12;

function Musicians() {
  const { t } = useTranslation();
  const { getTranslatedGenres, getTranslatedSkills, getTranslatedMusicianStatuses } = useMusicTranslations();
  const [musicians, setMusicians] = useState<MusicianWithDistance[]>([]);
  const [featuredMusicians, setFeaturedMusicians] = useState<MusicianWithDistance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const { coordinates, loading: locationLoading } = useLocation();

  // Load featured musicians once on mount
  useEffect(() => {
    const loadFeaturedMusicians = async () => {
      try {
        const featured = await fetchFeaturedMusicians(5, coordinates);
        setFeaturedMusicians(featured);
      } catch (error) {
        console.error('Error loading featured musicians:', error);
      }
    };

    loadFeaturedMusicians();
  }, [coordinates]);

  const loadMusicians = async (start = 0, replace = true) => {
    try {
      setLoadingMore(!replace);
      
      const { musicians: newMusicians, totalCount } = await fetchMusicians(
        start / ITEMS_PER_PAGE,
        ITEMS_PER_PAGE,
        searchQuery,
        selectedGenres,
        selectedSkills,
        selectedLocations,
        selectedStatuses,
        coordinates,
        nearMeEnabled
      );

      setMusicians(prev => replace ? newMusicians : [...prev, ...newMusicians]);
      setHasMore(totalCount ? start + ITEMS_PER_PAGE < totalCount : false);
    } catch (error) {
      console.error('Error loading musicians:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(0);
    loadMusicians(0);
  }, [searchQuery, selectedGenres, selectedSkills, selectedLocations, selectedStatuses, nearMeEnabled]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadMusicians(nextPage * ITEMS_PER_PAGE, false);
  };

  // Get unique locations from musicians
  const locations = Array.from(new Set(musicians.map(m => m.location).filter(Boolean)));

  const translatedGenres = getTranslatedGenres();
  const translatedSkills = getTranslatedSkills();
  const translatedMusicianStatuses = getTranslatedMusicianStatuses();

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev =>
      prev.includes(skill)
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const handleLocationToggle = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedGenres([]);
    setSelectedSkills([]);
    setSelectedLocations([]);
    setSelectedStatuses([]);
    setNearMeEnabled(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title subtitle={t('musicians.subtitle')}>
          {t('musicians.title')}
        </Title>
        <div className="text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Musiker in Deiner Nähe entdecken"
        description="Finde talentierte Musiker in der Schweiz. Vernetze dich mit Musikern, entdecke neue Talente und starte gemeinsame Projekte. Über 500 Musiker aus allen Genres und Fähigkeiten."
        keywords={['Musiker Schweiz', 'Schweizer Musiker', 'Musiker finden', 'Musik Schweiz', 'Band Mitglieder', 'Musikersuche']}
        url="https://jamnation.ch/musicians"
      />

      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title 
          subtitle={t('musicians.subtitle')}
        >
          {t('musicians.title')}
        </Title>

        {featuredMusicians.length > 0 && (
          <div className="mb-24">
            <Headline title={t('musicians.sections.newMusicians')} />
            <Swiper>
              {featuredMusicians.map((musician) => (
                <MusicianPanel key={musician.id} musician={musician} />
              ))}
            </Swiper>
          </div>
        )}

        <div>
          <div className="max-w-4xl mx-auto mb-8">
            <div className="text-xs text-gray-400 mb-2">FILTER</div>
            <Filters
              searchPlaceholder={t('musicians.filters.searchPlaceholder')}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              filterGroups={[
                {
                  name: t('musicians.filters.genres'),
                  options: translatedGenres.map(genre => ({
                    value: genre.value,
                    label: genre.label,
                    count: musicians.filter(m => m.genres?.includes(genre.value)).length,
                  })),
                  selected: selectedGenres,
                  onToggle: handleGenreToggle,
                },
                {
                  name: t('musicians.filters.skills'),
                  options: translatedSkills.map(skill => ({
                    value: skill.value,
                    label: skill.label,
                    count: musicians.filter(m => m.skills?.includes(skill.value)).length,
                  })),
                  selected: selectedSkills,
                  onToggle: handleSkillToggle,
                },
                {
                  name: t('musicians.filters.location'),
                  options: locations.map(location => ({
                    value: location || '',
                    label: location || '',
                    count: musicians.filter(m => m.location === location).length,
                  })),
                  selected: selectedLocations,
                  onToggle: handleLocationToggle,
                },
                {
                  name: 'Status',
                  options: translatedMusicianStatuses.map(status => ({
                    value: status.value,
                    label: status.label,
                    count: musicians.filter(m => m.status === status.value).length,
                  })),
                  selected: selectedStatuses,
                  onToggle: handleStatusToggle,
                },
              ]}
              onClearAll={handleClearFilters}
              nearMeFilter={{
                enabled: nearMeEnabled,
                onToggle: () => setNearMeEnabled(!nearMeEnabled),
                available: !locationLoading && !!coordinates,
              }}
            />
          </div>

          <Headline title={t('musicians.sections.allMusicians')} />
          <Grid cols={4}>
            {musicians.map((musician) => (
              <MusicianPanel key={musician.id} musician={musician} nearMeEnabled={nearMeEnabled} />
            ))}
          </Grid>

          {musicians.length === 0 && (
            <div className="text-center text-light/60 py-12">
              No musicians found.
            </div>
          )}

          <LoadMore
            onClick={handleLoadMore}
            loading={loadingMore}
            hasMore={hasMore}
          />
        </div>
      </div>
     </>
  );
}

export default Musicians;